import { resourcesApi } from '../services/resources-api';

export type OnDocumentSelectCallback = (docId: string, source: string) => void;

// Local type for Discovery
interface DiscoveryConnection {
  resource1: { id: string; title: string; category: string };
  resource2: { id: string; title: string; category: string };
  score: number;
}

export class ConnectionsDiscovery {
  private panelEl: HTMLElement;
  private contentEl: HTMLElement;
  private isOpen: boolean = false;
  private isLoading: boolean = false;
  private discoveries: DiscoveryConnection[] = [];
  private onDocumentSelect: OnDocumentSelectCallback | null = null;

  constructor() {
    this.createPanel();
    this.panelEl = document.getElementById('connections-discovery-panel')!;
    this.contentEl = document.getElementById('connections-discovery-content')!;
    this.setupEventListeners();
  }

  private createPanel(): void {
    const panel = document.createElement('div');
    panel.id = 'connections-discovery-panel';
    panel.className = 'connections-discovery-panel hidden';
    panel.innerHTML = `
      <div class="connections-discovery-container">
        <div class="connections-discovery-header">
          <h3>Hidden Connections</h3>
          <div class="connections-discovery-actions">
            <button id="connections-refresh-btn" class="connections-btn" title="Refresh">
              &#8635;
            </button>
            <button id="connections-close-btn" class="connections-btn" title="Close">
              &times;
            </button>
          </div>
        </div>
        <div id="connections-discovery-content" class="connections-discovery-content">
          <div class="connections-discovery-hint">
            Click refresh to find hidden connections between your documents.
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(panel);
  }

  private setupEventListeners(): void {
    // Close button
    document.getElementById('connections-close-btn')?.addEventListener('click', () => {
      this.close();
    });

    // Refresh button
    document.getElementById('connections-refresh-btn')?.addEventListener('click', () => {
      this.loadDiscoveries();
    });

    // Click outside to close
    this.panelEl.addEventListener('click', (e) => {
      if (e.target === this.panelEl) {
        this.close();
      }
    });

    // Keyboard shortcut: Escape to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }

  setOnDocumentSelect(callback: OnDocumentSelectCallback): void {
    this.onDocumentSelect = callback;
  }

  open(): void {
    this.isOpen = true;
    this.panelEl.classList.remove('hidden');

    // Load discoveries if we don't have any
    if (this.discoveries.length === 0 && !this.isLoading) {
      this.loadDiscoveries();
    }
  }

  close(): void {
    this.isOpen = false;
    this.panelEl.classList.add('hidden');
  }

  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  private async loadDiscoveries(): Promise<void> {
    if (this.isLoading) return;

    this.isLoading = true;
    this.renderLoading();

    try {
      const response = await resourcesApi.discoveries({ limit: 15, minScore: 0.6 });

      if (response.success && response.data) {
        this.discoveries = response.data.discoveries;
        this.renderDiscoveries();
      } else {
        this.renderError(response.error || 'Failed to load discoveries');
      }
    } catch (error: any) {
      this.renderError(error.message || 'Network error');
    } finally {
      this.isLoading = false;
    }
  }

  private renderLoading(): void {
    this.contentEl.innerHTML = `
      <div class="connections-discovery-loading">
        <div class="connections-discovery-spinner"></div>
        <p>Finding hidden connections...</p>
      </div>
    `;
  }

  private renderError(message: string): void {
    this.contentEl.innerHTML = `
      <div class="connections-discovery-error">
        <p>Error: ${message}</p>
        <button class="connections-retry-btn">Retry</button>
      </div>
    `;

    this.contentEl.querySelector('.connections-retry-btn')?.addEventListener('click', () => {
      this.loadDiscoveries();
    });
  }

  private renderDiscoveries(): void {
    if (this.discoveries.length === 0) {
      this.contentEl.innerHTML = `
        <div class="connections-discovery-empty">
          <p>No hidden connections found yet.</p>
          <p class="connections-hint">Add more documents with different categories to find connections.</p>
        </div>
      `;
      return;
    }

    const html = this.discoveries.map((connection, index) => {
      const scorePercent = Math.round(connection.score * 100);

      return `
        <div class="connection-item" data-index="${index}">
          <div class="connection-score">${scorePercent}%</div>
          <div class="connection-docs">
            <div class="connection-doc" data-doc-id="${connection.resource1.id}" data-source="resource">
              <span class="connection-doc-source">&#128196;</span>
              <span class="connection-doc-title">${connection.resource1.title}</span>
              <span class="connection-doc-category">${connection.resource1.category}</span>
            </div>
            <div class="connection-link-icon">&#8596;</div>
            <div class="connection-doc" data-doc-id="${connection.resource2.id}" data-source="resource">
              <span class="connection-doc-source">&#128196;</span>
              <span class="connection-doc-title">${connection.resource2.title}</span>
              <span class="connection-doc-category">${connection.resource2.category}</span>
            </div>
          </div>
        </div>
      `;
    }).join('');

    this.contentEl.innerHTML = `
      <div class="connections-discovery-list">
        ${html}
      </div>
    `;

    // Add click handlers to documents
    this.contentEl.querySelectorAll('.connection-doc').forEach(docEl => {
      docEl.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const docId = target.dataset.docId;
        const source = target.dataset.source;

        if (docId && source && this.onDocumentSelect) {
          this.onDocumentSelect(docId, source);
          this.close();
        }
      });
    });
  }
}
